import type { EditorCommand } from '../models/editor-command';
import type { AlignEdge, DistributeAxis } from './arrangement';

/**
 * The align and distribute entries of the command registry, for a given selection.
 *
 * Their own file because whether they exist at all is a decision, not a detail: the palette,
 * the context menu and the bulk-actions panel all have to agree about it, and the editor
 * component is too large to test that agreement through.
 */

export interface ArrangeCommandOptions {
  /**
   * Whether lining things up applies to this selection.
   *
   * False once a desk or a desk image is in it. Desks are placed by the desk tools, spaced by
   * the group panel and nudged by hand; an edge alignment across them moves bookable seats by
   * the floor-load behind one icon, at a spacing none of those tools would have chosen.
   */
  readonly alignable: boolean;
  readonly selectedCount: number;
  readonly onAlign: (edge: AlignEdge) => void;
  readonly onDistribute: (axis: DistributeAxis) => void;
}

const ALIGN_EDGES: ReadonlyArray<[AlignEdge, string]> = [
  ['left', 'floorplan-editor-align-left'],
  ['centreX', 'floorplan-editor-align-centre-x'],
  ['right', 'floorplan-editor-align-right'],
  ['top', 'floorplan-editor-align-top'],
  ['centreY', 'floorplan-editor-align-centre-y'],
  ['bottom', 'floorplan-editor-align-bottom'],
];

const DISTRIBUTE_AXES: ReadonlyArray<[DistributeAxis, string]> = [
  ['x', 'floorplan-editor-distribute-x'],
  ['y', 'floorplan-editor-distribute-y'],
];

/**
 * Nothing at all for a selection these do not apply to, rather than a list of refusals.
 *
 * The palette is a searchable index of what the editor can do, and a greyed-out "align left" in
 * it raises the question of what would enable it — to which the answer, "select something that is
 * not a desk", is not a thing anybody was trying to do. The context menu is built from this same
 * registry by id, so leaving them out here keeps the two in step without a second rule.
 *
 * Still greyed rather than absent for a selection too small to align: there the answer is
 * "select one more", which is worth telling somebody.
 */
export const arrangeCommands = ({
  alignable,
  selectedCount,
  onAlign,
  onDistribute,
}: ArrangeCommandOptions): EditorCommand[] => {
  if (!alignable) {
    return [];
  }

  return [
    ...ALIGN_EDGES.map(([edge, labelKey]): EditorCommand => ({
      id: `align-${edge}`,
      labelKey,
      group: 'arrange',
      // Aligning one thing to itself does nothing.
      disabled: selectedCount < 2,
      run: (): void => onAlign(edge),
    })),
    ...DISTRIBUTE_AXES.map(([axis, labelKey]): EditorCommand => ({
      id: `distribute-${axis}`,
      labelKey,
      group: 'arrange',
      // Three is the fewest with a middle to move; with two there is nothing between the ends.
      disabled: selectedCount < 3,
      run: (): void => onDistribute(axis),
    })),
  ];
};

export default arrangeCommands;
